// utils/chatInputs.ts
// ************************* Utils function for Flow execution (inputs management) *************************

export const SUPPORTED_IMAGE_TYPES = {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/gif': ['.gif'],
    'image/webp': ['.webp']
  };
  
  export const SUPPORTED_AUDIO_TYPES = {
    'audio/mp3': ['.mp3'],
    'audio/mp4': ['.mp4'],
    'audio/mpeg': ['.mpeg', '.mpga'],
    'audio/m4a': ['.m4a'],
    'audio/wav': ['.wav'],
    'audio/webm': ['.webm']
  };
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // Add File to the type imports if not already present
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  export const parseInputValue = (type: string, value: string | boolean | number | File | any): any => {
    switch (type) {
      case 'string':
        return value.toString();
      case 'number':
        return Number(value);
      case 'boolean':
        return Boolean(value);
      case 'object':
      case 'string[]':
      case 'number[]':
      case 'object[]':
      case 'ChatMessage[]':
        // For these types, we'll return the raw string input
        // The actual parsing will be done when sending the message
        return value.toString();
        
      case 'image':
        if (value instanceof File && isImageFile(value)) {
          return value;
        } else if (typeof value === 'string' && value.startsWith('data:image/')) {
          return value;
        }
        throw new Error('Invalid image format');
      case 'image[]':
        if (Array.isArray(value)) {
          // Handle array of Files
          if (value.every(v => v instanceof File && isImageFile(v))) {
            return value;
          }
          // Handle array of base64 strings
          if (value.every(v => typeof v === 'string' && v.startsWith('data:image/'))) {
            return value;
          }
        } else if (value instanceof FileList) {
          // Handle FileList from input[type="file"]
          return Array.from(value).filter(file => isImageFile(file));
        }
        throw new Error('Invalid image array format');
  
      case 'audio':
        if (value instanceof File && isAudioFile(value)) {
          return value;
        } else if (typeof value === 'string' && value.startsWith('data:audio/')) {
          return value;
        }
        throw new Error('Invalid audio format');
      case 'audio[]':
        if (Array.isArray(value)) {
          if (value.every(v => v instanceof File && isAudioFile(v))) {
            return value;
          }
          if (value.every(v => typeof v === 'string' && v.startsWith('data:audio/'))) {
            return value;
          }
        } else if (value instanceof FileList) {
          return Array.from(value).filter(file => isAudioFile(file));
        }
        throw new Error('Invalid audio array format');
  
      case 'pdf':
      case 'pptx':
      case 'markdown':
      case 'csv':
      case 'json':
      case 'docx':
      case 'odt':
        // For file types, return the value as is
        return value;
      default:
        return value;
    }
  };
  
  // Function used to format input before executing the workflow
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  export const processInputValue = (value: any): any => {
    if (value instanceof File) {
      // Single File object should be handled separately
      return value;
    }
  
    if (Array.isArray(value)) {
      // Handle arrays, including arrays of Files or base64 strings
      if (value.every(v => v instanceof File || (typeof v === 'string' && v.startsWith('data:')))) {
        return value; // Keep arrays of Files or base64 strings as is
      }
      return value.map(item => typeof item === 'string' ? item.trim() : processInputValue(item));
    }
  
    if (typeof value === 'string') {
      // For base64 images or other files, return as is
      if (value.startsWith('data:')) {
        return value;
      }
      // Attempt to parse as JSON, but return the original string if parsing fails
      try {
        return JSON.parse(value);
      } catch {
        return value.trim();
      }
    }
  
    if (typeof value === 'object' && value !== null) {
      return Object.fromEntries(
        Object.entries(value).map(([k, v]) => [k, processInputValue(v)])
      );
    }
    return value;
  };
  
  export const getDefaultValueForType = (type: string) => {
    switch (type) {
      case 'string':
        return '';
      case 'number':
        return 1;
      case 'boolean':
        return false;
      case 'object':
        return '{"property": "value"}';
      case 'string[]':
        return '["A", "B"]';
      case 'number[]':
        return '[1, 2]';
      case 'object[]':
        return '[ {"property": "value"}, {"property": "value"} ]';
      case 'ChatMessage[]':
        return [];
      case 'image[]':
      case 'audio[]':
        return [];
      case 'pdf':
      case 'pptx':
      case 'markdown':
      case 'csv':
      case 'json':
      case 'docx':
      case 'odt':
      case 'image':
      case 'audio':
        return '';
      default:
        return '[ {"role": "user", "content": "message"} ]';
    }
  };
  
  // Function to get accepted file types based on the input type
  export const getAcceptedFileTypes = (type: string): string => {
    switch (type) {
      case 'image':
      case 'image[]':
        return Object.values(SUPPORTED_IMAGE_TYPES).flat().join(',');
      case 'audio':
      case 'audio[]':
        return Object.values(SUPPORTED_AUDIO_TYPES).flat().join(',');
      case 'pdf':
        return '.pdf';
      case 'pptx':
        return '.pptx';
      case 'markdown':
        return '.md,.markdown';
      case 'csv':
        return '.csv';
      case 'json':
        return '.json';
      case 'docx':
        return '.docx';
      case 'odt':
        return '.odt';
      default:
        return '';
    }
  };
  
  export const isImageFile = (file: File): boolean => {
    return Object.keys(SUPPORTED_IMAGE_TYPES).includes(file.type);
  };
  
  export const isAudioFile = (file: File): boolean => {
    return Object.keys(SUPPORTED_AUDIO_TYPES).includes(file.type);
  };
  
  export const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          // For images, we use a simpler base64 format
          if (isImageFile(file)) {
            resolve(`data:${file.type};base64,${reader.result.split(',')[1]}`);
          }
          // Handle audio files
          else if (isAudioFile(file)) {
            const base64WithMetadata = `data:${file.type};filename=${file.name};base64,${reader.result.split(',')[1]}`;
            resolve(base64WithMetadata);
          }
          else {
            // For other files, keep the filename metadata
            const base64WithMetadata = `data:${file.type};filename=${file.name};base64,${reader.result.split(',')[1]}`;
            resolve(base64WithMetadata);
          }
        } else {
          reject(new Error('Failed to convert file to base64'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };